import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Box, CircularProgress, Typography, Chip } from '@mui/material';
import type { AgentMessageDto } from '../../types/session';
import { MessageBubble } from './MessageBubble';
import { StreamingMessage } from './StreamingMessage';
import { ToolCallIndicator } from './ToolCallIndicator';
import { SubAgentCard } from '../orchestrator/SubAgentCard';
import type { OrchestratorEvent } from '../orchestrator/useOrchestratorStream';
import { signalRService } from '../../services/signalRService';
import type { SignalREvents } from '../../services/signalRService';
import { useSignalR } from '../../providers/SignalRProvider';
import { useNotificationStore } from '../../store/notificationStore';

interface MessageListWithStreamingProps {
  messages: AgentMessageDto[];
  sessionId: string;
  loading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  onStreamingComplete?: (content: string) => void;
}

interface StreamingState {
  content: string;
  isStreaming: boolean;
  toolCalls: Array<{ id: string; name: string; arguments: string; status: 'pending' | 'executing' | 'completed' | 'failed' }>;
}

/**
 * Enhanced message list component with SignalR streaming support.
 */
export const MessageListWithStreaming = ({
  messages,
  sessionId,
  loading,
  onLoadMore,
  hasMore,
  onStreamingComplete,
}: MessageListWithStreamingProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [streamingState, setStreamingState] = useState<StreamingState | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'connecting'>('disconnected');
  const [isThinking, setIsThinking] = useState(false);
  const [subAgentEvents, setSubAgentEvents] = useState<OrchestratorEvent[]>([]);
  const subAgentEventIdRef = useRef(0);
  const { agentExecutionStatus } = useSignalR();
  const showNotification = useNotificationStore((s) => s.showNotification);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingState, scrollToBottom]);

  useEffect(() => {
    setConnectionStatus(agentExecutionStatus);

    if (agentExecutionStatus !== 'connected') {
      return;
    }

    // Join session and set up streaming events once connected.
    void signalRService.joinSession(sessionId).catch((error) => {
      console.error('Error joining SignalR session:', error);
      setConnectionStatus('disconnected');
    });

    const pushSubAgentEvent = (type: OrchestratorEvent['type'], callId: string, data: Record<string, unknown>) => {
      setSubAgentEvents((prev) => [
        ...prev,
        { id: `sa-${++subAgentEventIdRef.current}`, type, callId, data, timestamp: Date.now() },
      ]);
    };

    const events: SignalREvents = {
      onMessageReceived: (data) => {
        if (data.sessionId === sessionId) {
          setIsThinking(true);
        }
      },
      onAgentThinking: (data) => {
        if (data.sessionId === sessionId) {
          setIsThinking(true);
          setStreamingState({ content: '', isStreaming: true, toolCalls: [] });
        }
      },
      onTokenReceived: (data) => {
        if (data.sessionId === sessionId) {
          setIsThinking(false);
          setStreamingState((prev) => {
            if (!prev) {
              return { content: data.token, isStreaming: true, toolCalls: [] };
            }
            return { ...prev, content: prev.content + data.token, isStreaming: true };
          });
        }
      },
      onToolCall: (data) => {
        if (data.sessionId === sessionId) {
          setStreamingState((prev) => {
            if (!prev) {
              return { content: '', isStreaming: true, toolCalls: [] };
            }
            const toolCall = {
              id: `tool-${Date.now()}`,
              name: data.toolName,
              arguments: data.arguments,
              status: 'executing' as const,
            };
            return {
              ...prev,
              toolCalls: [...prev.toolCalls, toolCall],
            };
          });
        }
      },
      onSubAgentStarted: (data) => {
        if (data.sessionId === sessionId) {
          pushSubAgentEvent('sub_agent_started', data.callId, {
            alias: data.alias,
            depth: data.depth,
            task: data.task,
          });
        }
      },
      onSubAgentTokenReceived: (data) => {
        if (data.sessionId === sessionId) {
          pushSubAgentEvent('sub_agent_token', data.callId, { token: data.token });
        }
      },
      onSubAgentToolCall: (data) => {
        if (data.sessionId === sessionId) {
          pushSubAgentEvent('sub_agent_tool_call', data.callId, {
            toolName: data.toolName,
            arguments: data.arguments,
          });
        }
      },
      onSubAgentCompleted: (data) => {
        if (data.sessionId === sessionId) {
          pushSubAgentEvent('sub_agent_completed', data.callId, {
            result: data.result,
            success: data.success,
            errorMessage: data.errorMessage,
          });
        }
      },
      onResponseComplete: (data) => {
        if (data.sessionId === sessionId) {
          setIsThinking(false);
          onStreamingComplete?.(data.response ?? '');
          setStreamingState(null);
          setSubAgentEvents([]);
        }
      },
      onExecutionError: (data) => {
        if (data.sessionId !== sessionId) {
          return;
        }
        // Surface the backend error to the user as a top toast notification
        // so they know their message failed instead of silently losing the
        // "thinking" indicator.
        const message = data.errorMessage?.trim()
          ? data.errorMessage
          : 'An error occurred while processing your message.';
        showNotification(message, 'error', 8000);

        // Clear in-flight streaming UI; the matching ResponseComplete event
        // (sent by the server right after) will also reload messages.
        setIsThinking(false);
        setStreamingState(null);
        setSubAgentEvents([]);
      },
    };

    signalRService.on(events);

    return () => {
      signalRService.leaveSession(sessionId);
      signalRService.off();
    };
  }, [agentExecutionStatus, sessionId, onStreamingComplete, showNotification]);

  const handleScroll = () => {
    if (!containerRef.current || !onLoadMore || !hasMore || loading) return;

    const { scrollTop } = containerRef.current;
    if (scrollTop < 100) {
      onLoadMore();
    }
  };

  // Deduplicate messages by ID to prevent rendering the same message multiple times
  const uniqueMessages = useMemo(() => {
    const seen = new Set<string>();
    return messages.filter((m) => {
      if (seen.has(m.id)) return false;
      seen.add(m.id);
      return true;
    });
  }, [messages]);

  const subAgentGroups = useMemo(() => {
    const map = new Map<string, OrchestratorEvent[]>();
    const topLevelCallIds: string[] = [];
    for (const evt of subAgentEvents) {
      if (!evt.callId) continue;
      let group = map.get(evt.callId);
      if (!group) {
        group = [];
        map.set(evt.callId, group);
        topLevelCallIds.push(evt.callId);
      }
      group.push(evt);
    }
    return { map, topLevelCallIds };
  }, [subAgentEvents]);

  if (uniqueMessages.length === 0 && !loading && !streamingState) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          p: 4,
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No messages yet. Start the conversation!
        </Typography>
        {connectionStatus !== 'connected' && (
          <Chip
            label={connectionStatus === 'connecting' ? 'Connecting...' : 'Disconnected'}
            color={connectionStatus === 'connecting' ? 'warning' : 'error'}
            size="small"
          />
        )}
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      onScroll={handleScroll}
      sx={{
        flex: 1,
        overflowY: 'auto',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Connection Status Indicator */}
      {connectionStatus !== 'connected' && (
        <Box sx={{ mb: 1, display: 'flex', justifyContent: 'center' }}>
          <Chip
            label={connectionStatus === 'connecting' ? 'Connecting to real-time updates...' : 'Real-time updates disconnected'}
            color={connectionStatus === 'connecting' ? 'warning' : 'error'}
            size="small"
          />
        </Box>
      )}

      {loading && uniqueMessages.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Scroll up to load more messages...
          </Typography>
        </Box>
      )}
      {uniqueMessages.map((message) => (
        <MessageBubble
          key={message.id}
          message={message}
        />
      ))}
      
      {/* Streaming Message */}
      {streamingState && (
        <Box sx={{ mb: 2 }}>
          {streamingState.toolCalls.length > 0 && (
            <Box sx={{ mb: 1 }}>
              {streamingState.toolCalls.map((toolCall) => (
                <ToolCallIndicator
                  key={toolCall.id}
                  toolName={toolCall.name}
                  arguments={toolCall.arguments}
                  status={toolCall.status}
                />
              ))}
            </Box>
          )}

          {subAgentGroups.topLevelCallIds.length > 0 && (
            <Box sx={{ mb: 1 }}>
              {subAgentGroups.topLevelCallIds.map((callId) => (
                <SubAgentCard
                  key={callId}
                  callId={callId}
                  events={subAgentGroups.map.get(callId) ?? []}
                  allEvents={subAgentGroups.map}
                />
              ))}
            </Box>
          )}

          <StreamingMessage
            content={streamingState.content}
            isStreaming={streamingState.isStreaming || isThinking}
          />
        </Box>
      )}
      
      <div ref={messagesEndRef} />
    </Box>
  );
};
